// Companion/blocks/StudySetBlock.tsx
//
// A study set served into the thread (ORA-677).
//
// A set is one block, whatever it holds: a few questions, a small deck, a video
// or two — plus a footer that always says what the set is and offers the rest
// of it in the full study UI. That shape is what lets the companion answer
// "give me some renal questions" with something the student can start on the
// spot and finish properly, instead of a link.
//
// The items are the *same* components the study surfaces and the single-content
// blocks use, so a question answered here counts once, and a card rated here is
// a real review. All this adapter adds is the set: which item is showing, what
// is done, and the set id that rides on every write.
//
// A finished set folds away on Done (ORA-833), leaving the same one-line
// summary the server writes when the student comes back from a full session —
// unless the thread already shows that row, which is the one case where the
// fold would say the same thing twice. Folding hides the set, it does not take
// it down: everything the student did in the thread lives in the state of these
// components, so unmounting them would hand back a blank, re-answerable set.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  selectHasSetResultRow,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type {
  ChatStudySetBlockPayload,
  ChatStudySetProgress,
} from '@/types/chatBlocks';
import type { ChatSurface } from '@/types/chatEnvelope';
import { QuestionBlockLimits } from './QuestionBlock';
import StudySetFlashcardDeck from './StudySetFlashcardDeck';
import StudySetFooter from './StudySetFooter';
import StudySetHeader from './StudySetHeader';
import StudySetLeaveModal from './StudySetLeaveModal';
import StudySetQuestionPager, {
  INERT_PROPS,
  StudySetQuestionProgress,
  type StudySetQuestionItem,
} from './StudySetQuestionPager';
import StudySetVideoStage from './StudySetVideoStage';
import type { ChatQuestionFollowup } from './chatQuestionFollowup';
import { useBlockAnchor } from './useBlockAnchor';
import { useQuestionStudyBadges } from './useQuestionStudyBadges';
import {
  captureStudySetEvent,
  isInlineStudySet,
  studySetEventProps,
} from './studySetAnalytics';
import { isSetComplete, setItemsNoun, setResultText } from './setResultText';
import { useStudySetLaunch } from './useStudySetLaunch';

/**
 * Sets this page session has already reported as served. The thread re-mounts a
 * block on every history rebuild, tab change and drawer reopen, and a set is
 * served once however many times it is drawn.
 */
const reportedServedSetIds = new Set<string>();

export interface StudySetBlockProps {
  payload: ChatStudySetBlockPayload;
  block: ChatBlock;
  surface: ChatSurface;
  isReadOnly: boolean;
  onAnsweredChange?: (
    answered: boolean,
    question?: ChatQuestionFollowup
  ) => void;
  onBeforeContentExpand?: () => void;
}

const StudySetBlock = ({
  payload,
  block,
  surface,
  isReadOnly,
  onAnsweredChange,
  onBeforeContentExpand,
}: StudySetBlockProps) => {
  const { claim, retract } = useBlockAnchor(isReadOnly);

  /**
   * Whether the thread already carries the server's own result row for this set
   * — the one it writes when the student comes back from a launched session.
   * That is the line a fold leaves behind, so a set with one keeps "Done" as
   * the label for its state rather than printing the same summary twice. It is
   * read from the conversation rather than from the payload's status, because a
   * set finished in the thread comes back `completed` too and has no row.
   */
  const hasResultRow = useSelector((state: ChatRootState) =>
    selectHasSetResultRow(state, block.conversationId, payload.setId)
  );

  // The contract's rule, not `items.length`: a lazy set carries its items once
  // it has been drawn, and those belong to the full session rather than to the
  // thread.
  const items = useMemo(
    () => (isInlineStudySet(payload) ? (payload.items ?? []) : []),
    [payload]
  );

  const { confirmLeave, openLaunched, leaveModal } = useStudySetLaunch({
    block,
    surface,
  });

  // A missed question in a set offers the same two badges QBank's review does,
  // and studying one mints a review set of its own (ORA-830/831). The set is
  // what names the question: inside a set the questions have no block of their
  // own, so the add-block route a served question uses cannot serve them.
  const badges = useQuestionStudyBadges({
    block,
    isReadOnly,
    source: { kind: 'study_set', setId: payload.setId },
  });

  // The top of the funnel: the set reached the student. Reported from the
  // block rather than the stream so a set rebuilt into the thread on a reload
  // is not counted twice, and so a set that never rendered is not counted at
  // all.
  useEffect(() => {
    if (reportedServedSetIds.has(payload.setId)) return;
    reportedServedSetIds.add(payload.setId);

    captureStudySetEvent(
      'study_set_served',
      studySetEventProps(payload, surface)
    );
  }, [payload, surface]);

  /**
   * Items the student has finished, seeded with the ones the server had already
   * completed. The server owns the truth — `progress` is re-sent whenever the
   * set is read back — but a question answered in the thread must move the
   * count immediately rather than at the next launch, and an item that arrived
   * done must not be counted a second time when it is finished here again.
   */
  const [doneOrds, setDoneOrds] = useState<Set<number>>(
    () => new Set(items.filter((item) => item.done).map((item) => item.ord))
  );

  /** Questions answered here, and whether each was right on the first try. */
  const [answers, setAnswers] = useState<Map<number, boolean>>(() => new Map());

  const questions = useMemo(
    () => items.flatMap((item) => (item.kind === 'question' ? [item] : [])),
    [items]
  );

  /**
   * Which question the pager is showing. It lives here rather than in the pager
   * because the header that displays it is the block's, and it opens on the
   * first question the student has not answered, so a set they half-finished in
   * the full session picks up where they left it.
   */
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(() => {
    const firstUnanswered = questions.findIndex((item) => !item.done);

    return firstUnanswered >= 0
      ? firstUnanswered
      : Math.max(questions.length - 1, 0);
  });

  const videos = useMemo(
    () =>
      items.flatMap((item) =>
        item.kind === 'video'
          ? [{ ord: item.ord, done: item.done, video: item.video }]
          : []
      ),
    [items]
  );

  /**
   * Which video the stage is showing, owned here for the same reason the
   * question index is: the header prints the position. It opens on the first
   * video the student has not finished, so coming back to a half-watched set
   * puts the next one on the stage rather than replaying the first.
   */
  const [activeVideoOrd, setActiveVideoOrd] = useState(
    () => (videos.find((item) => !item.done) ?? videos[0])?.ord ?? 0
  );

  /**
   * What the deck has left in its queues, reported up so the header can say it.
   * The block cannot work the counts out for itself — the run's queues live in
   * the deck's reducer and move with every rating — so the deck sends them, and
   * the same numbers are dropped when they have not changed rather than
   * re-rendering the block on every card.
   */
  const [deckCounts, setDeckCounts] = useState<{
    new: number;
    due: number;
  } | null>(null);

  /**
   * Folded away by the footer's "Done" (ORA-833). A finished set is a screenful
   * of answered questions the student has already read, and until now the only
   * way past it was to scroll.
   *
   * Local state, deliberately: the server payload is the truth about the set, so
   * a reload draws it open again and the student folds it a second time. Nothing
   * about the set changes when it is folded.
   */
  const [isCollapsed, setIsCollapsed] = useState(false);
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const showButtonRef = useRef<HTMLButtonElement>(null);
  const hasFolded = useRef(false);

  /**
   * Focus follows the fold. The button the student pressed is hidden by what
   * their press did, so without this a keyboard Tab restarts at the top of the
   * document rather than carrying on from the row they are looking at.
   */
  useEffect(() => {
    if (isCollapsed) {
      hasFolded.current = true;
      showButtonRef.current?.focus();

      return;
    }

    // Not on the first render: the set opens unfolded, and a block drawn into
    // the thread must not steal the focus from whatever the student is doing.
    if (hasFolded.current) doneButtonRef.current?.focus();
  }, [isCollapsed]);

  const onDeckCountsChange = useCallback(
    (counts: { new: number; due: number }) => {
      setDeckCounts((current) =>
        current && current.new === counts.new && current.due === counts.due
          ? current
          : counts
      );
    },
    []
  );

  const progress: ChatStudySetProgress = useMemo(() => {
    // The seeded ords are already in the server's count, so the two are the
    // same number until the student finishes something here — `max`, not a
    // sum, is what keeps a set that arrives half-done from counting twice.
    const done = Math.min(
      Math.max(payload.progress.done, doneOrds.size),
      payload.progress.total
    );

    const correctHere = [...answers.values()].filter(Boolean).length;

    const correct =
      payload.progress.correct === undefined && answers.size === 0
        ? undefined
        : (payload.progress.correct ?? 0) + correctHere;

    return {
      ...payload.progress,
      done,
      ...(correct === undefined ? {} : { correct }),
    };
  }, [answers, doneOrds, payload.progress]);

  const markDone = (ord: number) => {
    setDoneOrds((current) =>
      current.has(ord) ? current : new Set(current).add(ord)
    );
  };

  /**
   * A question answered in the pager: the set's count, its score, and the
   * follow-up chips that belong to the answer the turn served it with.
   */
  const onQuestionAnswered = (
    item: StudySetQuestionItem,
    firstAttemptCorrect: boolean
  ) => {
    markDone(item.ord);

    setAnswers((current) =>
      current.has(item.ord)
        ? current
        : new Map(current).set(item.ord, firstAttemptCorrect)
    );

    onAnsweredChange?.(true, {
      blockId: block.id,
      anchor: {
        type: 'question',
        id: item.question.vignetteId,
        ...(item.question.variantId
          ? { variantId: item.question.variantId }
          : {}),
      },
      firstAttemptCorrect,
    });
  };

  /**
   * The items, in the shape their kind wants: a deck for cards (ORA-677), a
   * pager for questions (ORA-685) and a stage with a rail for videos
   * (ORA-686). Every kind has one, so there is no generic fallback.
   */
  const renderItems = () => {
    if (items.length === 0) return null;

    if (payload.kind === 'flashcard') {
      const cards = items.flatMap((item) =>
        item.kind === 'flashcard' ? [{ item, card: item.card }] : []
      );

      if (cards.length === 0) return null;

      return (
        <StudySetFlashcardDeck
          setId={payload.setId}
          cards={cards.map((entry) => entry.card)}
          completedCardIds={cards
            .filter((entry) => entry.item.done)
            .map((entry) => entry.card.id)}
          onCountsChange={onDeckCountsChange}
          // A card counts once it has left the deck's queues, not on its first
          // rating: a card rated Again is coming back this session (ORA-687).
          onCardGraduated={(cardId) => {
            const rated = cards.find((entry) => entry.card.id === cardId);
            if (rated) markDone(rated.item.ord);
          }}
        />
      );
    }

    if (payload.kind === 'question') {
      if (questions.length === 0) return null;

      return (
        <StudySetQuestionPager
          setId={payload.setId}
          items={questions}
          answers={answers}
          activeIndex={activeQuestionIndex}
          onActiveIndexChange={setActiveQuestionIndex}
          claim={claim}
          onBeforeContentExpand={onBeforeContentExpand}
          onAnswerConfirmed={onQuestionAnswered}
          badges={badges}
        />
      );
    }

    if (payload.kind === 'video') {
      if (videos.length === 0) return null;

      // The one thing a fold takes down rather than hides: a `VideoPlayer`
      // behind `hidden` is not idle — it keeps playing, keeps fetching and
      // would report progress for a video nobody is watching
      // (`StudySetVideoStage`). Nothing is lost by it either: which video is on
      // the stage and which are watched both live here, on the block, and every
      // video of a foldable set is watched.
      if (isCollapsed) return null;

      return (
        <StudySetVideoStage
          setId={payload.setId}
          items={videos}
          surface={surface}
          doneOrds={doneOrds}
          activeOrd={activeVideoOrd}
          onActiveOrdChange={setActiveVideoOrd}
          onWatched={markDone}
          claim={claim}
        />
      );
    }

    return null;
  };

  /**
   * What the header shows on the right: where the student is in the set, in
   * whatever the kind's own terms are. Only a set with something to step
   * through has a position worth drawing — a one-item question or video set is
   * a served question or a served video — while a deck always has queues.
   */
  const videoPosition = videos.findIndex((item) => item.ord === activeVideoOrd);

  const headerAside =
    payload.kind === 'question' && questions.length > 1 ? (
      <StudySetQuestionProgress
        items={questions}
        answers={answers}
        activeIndex={activeQuestionIndex}
      />
    ) : payload.kind === 'video' && videos.length > 1 ? (
      <span
        className="text-xs text-fg-secondary"
        data-testid="study-set-video-position"
      >
        Video {Math.max(videoPosition, 0) + 1} of {videos.length}
      </span>
    ) : payload.kind === 'flashcard' && deckCounts ? (
      <span className="text-xs text-fg-secondary" data-testid="deck-counts">
        New {deckCounts.new} <span aria-hidden="true">·</span> Due{' '}
        {deckCounts.due}
      </span>
    ) : null;

  /**
   * Whether "Done" folds the set away, or stays the label for the state that it
   * was. Three things have to hold: the thread does not already show the
   * server's result row for this set, the set is genuinely complete (a filter
   * that drew nothing is `0 of 0`, which is not a finished set — `SetResultBlock`
   * would call it paused), and there is something drawn to fold.
   */
  const canFold =
    !hasResultRow &&
    items.length > 0 &&
    isSetComplete({ ...payload, progress });

  const resultLine = setResultText({ ...payload, progress });

  /**
   * Fold the set away, and hand the companion's anchor back to the surface. The
   * anchor named whichever item the student last engaged with, and folding is
   * the clearest signal they are finished with the set — for a video set the
   * claim in force also said `playing: true` for a player the fold takes down.
   *
   * Unfolding does not claim it back: an anchor is claimed from an engagement,
   * never from a render (see `useBlockAnchor`), so the next step, rating or play
   * publishes it again — and re-publishing the video's old claim would put a
   * video on the envelope as playing when nothing is.
   */
  const fold = () => {
    retract();
    setIsCollapsed(true);
  };

  return (
    <div
      className="flex flex-col"
      data-testid="study-set-block"
      data-set-id={payload.setId}
      data-set-kind={payload.kind}
      data-collapsed={isCollapsed || undefined}
    >
      {/* The set itself, which nothing unmounts. A question answered here and a
          card rated here live in the state of these components — seeded from
          the *server* payload, which an in-thread answer never writes back — so
          a fold that took them down would hand back a blank set the student
          could answer a second time, writing a second graded response and a
          second review. Folded means hidden exactly as the pager hides an
          inactive question: `hidden` for the eye and the layout, `inert` and
          `aria-hidden` for the keyboard and the screen reader. (A video stage
          is the exception, and says why in `renderItems`.) */}
      <div
        aria-hidden={isCollapsed ? true : undefined}
        className={cn('flex flex-col', isCollapsed && 'hidden')}
        data-testid="study-set-open"
        {...(isCollapsed ? INERT_PROPS : {})}
      >
        <StudySetHeader
          payload={payload}
          isReadOnly={isReadOnly}
          aside={headerAside}
        />
        {items.length > 0 ? (
          <div className="flex flex-col gap-3 p-3">
            {payload.kind === 'question' && payload.limits ? (
              <QuestionBlockLimits limits={payload.limits} />
            ) : null}
            {renderItems()}
          </div>
        ) : null}
        <StudySetFooter
          payload={payload}
          progress={progress}
          isReadOnly={isReadOnly}
          hasItems={items.length > 0}
          doneRef={doneButtonRef}
          onDone={canFold ? fold : undefined}
          onBeforeLaunch={confirmLeave}
          onLaunched={openLaunched}
        />
      </div>
      {isCollapsed ? (
        // The folded set: the line the server writes for a set finished in the
        // full session, with the way back into this one beside it. One padded
        // row laid out like the footer it replaces, which stacks below `mobile`
        // so the line — the whole point of folding — keeps its words on a
        // 390px phone.
        <div
          className="flex min-h-11 flex-col justify-center gap-1 px-3 py-2 mobile:flex-row mobile:items-center mobile:justify-between mobile:gap-3"
          data-testid="study-set-collapsed"
        >
          <p className="m-0 flex min-w-0 items-start gap-1.5 text-xs text-fg-secondary">
            <CheckCircle
              size={14}
              aria-hidden="true"
              className="mt-0.5 shrink-0"
            />
            <span className="min-w-0">{resultLine}</span>
          </p>
          <Button
            className="min-h-11 mobile:shrink-0"
            onClick={() => setIsCollapsed(false)}
            ref={showButtonRef}
            size="sm"
            type="button"
            variant="ghost"
          >
            Show {setItemsNoun(payload.kind)}
          </Button>
        </div>
      ) : null}
      {/* What folding did, for a student who cannot see that it happened: the
          set disappears from under the button they pressed, so the line it left
          behind is read out. Polite, and always mounted, because a live region
          that arrives with its text already in it is announced by nothing
          reliably. */}
      <span aria-live="polite" className="sr-only">
        {isCollapsed ? resultLine : ''}
      </span>
      <StudySetLeaveModal {...leaveModal} />
    </div>
  );
};

export default StudySetBlock;
